// Phone numbers per shipping country. Checkout locks the prefix to the shipping country
// and only accepts a number from that country, so the courier can always call the buyer.
//
// `pattern` matches the national number without the leading 0 (64 123 4567 → "641234567").
// The region has exact rules; other EU countries only get a length check.

export const PHONE_RULES = {
  BA: { flag: '🇧🇦', dial: '387', example: '61 123 456', pattern: /^(6\d{7,8}|[3-5]\d{7})$/, error: 'Enter a valid Bosnian phone number' },
  RS: { flag: '🇷🇸', dial: '381', example: '64 123 4567', pattern: /^(6\d{7,9}|[1-3]\d{7,8})$/, error: 'Enter a valid Serbian phone number' },
  CG: { flag: '🇲🇪', dial: '382', example: '67 123 456', pattern: /^[2-6]\d{7}$/, error: 'Enter a valid Montenegrin phone number' },
  HR: { flag: '🇭🇷', dial: '385', example: '91 234 5678', pattern: /^(9\d{7,8}|1\d{6,7}|[2-5]\d{7})$/, error: 'Enter a valid Croatian phone number' },
};

const EU_DIAL = {
  AT: '43', BE: '32', BG: '359', CY: '357', CZ: '420', DE: '49', DK: '45', EE: '372', ES: '34',
  FI: '358', FR: '33', GR: '30', HU: '36', IE: '353', IT: '39', LT: '370', LU: '352', LV: '371',
  MT: '356', NL: '31', PL: '48', PT: '351', RO: '40', SE: '46', SI: '386', SK: '421',
};

// "DE" → 🇩🇪
const flagOf = (code) => String.fromCodePoint(...[...code].map(c => 0x1F1A5 + c.charCodeAt(0)));

for (const [code, dial] of Object.entries(EU_DIAL)) {
  PHONE_RULES[code] = {
    flag: flagOf(code),
    dial,
    example: ' ', // non-empty, or the floating label never drops back into the field
    pattern: /^\d{6,12}$/,
    error: 'Invalid phone number',
    international: true,
    // Italian numbers keep their leading 0 after +39 (landlines: +39 06…)
    keepLeadingZero: code === 'IT',
  };
}

const isRegional = (country) => !phoneRuleFor(country).international;

export const phoneRuleFor = (country) => PHONE_RULES[country] || PHONE_RULES.BA;

/**
 * The national number as digits, whatever the buyer typed: "+381 64/123-4567",
 * "00381641234567" and "064 123 4567" all give "641234567".
 */
export function nationalDigits(phone, country) {
  const { dial, keepLeadingZero } = phoneRuleFor(country);
  let digits = String(phone || '').replace(/\D/g, '');
  const international = /^\s*(\+|00)/.test(String(phone || ''));

  if (digits.startsWith('00' + dial)) {
    digits = digits.slice(2 + dial.length);
  } else if (digits.startsWith(dial) && (international || digits.length > dial.length + 7)) {
    // "+381…", or "381…" typed without the + (longer than any national number)
    digits = digits.slice(dial.length);
  }
  return keepLeadingZero ? digits : digits.replace(/^0+/, '');
}

/** null when valid, otherwise the English error message (pass it through translate). */
export function phoneError(phone, country) {
  const rule = phoneRuleFor(country);
  const digits = String(phone || '').replace(/\D/g, '');
  if (!digits) return 'This field is required';

  // Another country's prefix typed in — reject instead of guessing.
  const typed = String(phone).trim();
  const international = typed.startsWith('+') || digits.startsWith('00');
  const ownPrefix = digits.startsWith(rule.dial) || digits.startsWith('00' + rule.dial);
  if (international && !ownPrefix) return rule.error;

  return rule.pattern.test(nationalDigits(phone, country)) ? null : rule.error;
}

/**
 * What the order stores. The region keeps the local format its couriers already get
 * ("0641234567"); EU orders go through Fulfillio, so they're stored internationally.
 */
export const localPhone = (phone, country) => isRegional(country)
  ? '0' + nationalDigits(phone, country)
  : '+' + phoneRuleFor(country).dial + nationalDigits(phone, country);
